/**
 * Single source of truth for the Custom Perks showcase.
 *
 * Asset contract (served from `public/`):
 *  - Banner:    `/perks/banners/<perkKey>.webp`
 *  - Tier icon: `/perks/icons/<perkKey>/<tier>.webp`
 *
 * Tiers are ordered low -> exotic via TIER_ORDER and are used everywhere in that order.
 */

import weaponData from './perkWeapons.json';
import bonusData from './perkBonuses.json';

/**
 * Prestige tiers in the exact order baked into the perk banner art.
 * Each tier spans 10 perk levels: Iron = 1-10, Bronze = 11-20, ... Atomic = 141-150.
 */
export const TIER_ORDER = [
  'iron',
  'bronze',
  'copper',
  'silver',
  'gold',
  'emerald',
  'sapphire',
  'amethyst',
  'ruby',
  'diamond',
  'obsidian',
  'mythril',
  'void',
  'celestial',
  'atomic',
] as const;

export type Tier = (typeof TIER_ORDER)[number];

/** Perk levels covered by each prestige tier (10 levels per tier). */
export const LEVELS_PER_TIER = 10;

/**
 * Per-rank brand color for tier/weapon badges (keyed by capitalized tier label).
 * Mirrors the prestige tier theme.
 */
export const TIER_COLORS: Record<string, string> = {
  Iron: '#9ca3af',
  Bronze: '#cd7f32',
  Copper: '#d2691e',
  Silver: '#cbd5e1',
  Gold: '#fbbf24',
  Emerald: '#34d399',
  Sapphire: '#3b82f6',
  Amethyst: '#a855f7',
  Ruby: '#fb2c54',
  Diamond: '#67e8f9',
  Obsidian: '#6b7280',
  Mythril: '#7dd3fc',
  Void: '#8b5cf6',
  Celestial: '#fde68a',
  Atomic: '#f97316',
};

/** A weapon a perk unlocks, extracted from the ULMod veterancy source. */
export interface PerkWeapon {
  name: string;
  class: string;
  unlockLevel: number;
  rank: string;
  damage: number | null;
  isDefault: boolean;
  isSpawn: boolean;
  unlockText: string;
}

/** A single prestige tier entry for a perk (icon + display label + level range). */
export interface PerkTier {
  tier: Tier;
  label: string;
  src: string;
  minLevel: number;
  maxLevel: number;
  /** Pre-formatted level range, e.g. "1-10". */
  levelRange: string;
}

/** A custom perk with its banner, prestige-tier icons, weapons and marketing copy. */
export interface Perk {
  key: string;
  name: string;
  tagline: string;
  description: string;
  bannerSrc: string | null;
  icons: PerkTier[];
  weapons: PerkWeapon[];
  /** Player-facing bonus strings per rank, aligned to TIER_ORDER (15 entries). */
  bonuses: string[][];
  comingSoon?: boolean;
}

/** Capitalize a tier name for display ("iron" -> "Iron"). */
function label(tier: string): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

/** Build the 15 prestige-tier icon entries for a perk key (with level ranges). */
function buildTiers(key: string): PerkTier[] {
  return TIER_ORDER.map((tier, i) => {
    const minLevel = i * LEVELS_PER_TIER + 1;
    const maxLevel = minLevel + LEVELS_PER_TIER - 1;
    return {
      tier,
      label: label(tier),
      src: `/perks/icons/${key}/${tier}.webp`,
      minLevel,
      maxLevel,
      levelRange: `${minLevel}-${maxLevel}`,
    };
  });
}

// TODO: refine copy — taglines (<=6 words) and descriptions (1-2 sentences) below.
const PERK_COPY: Record<
  string,
  { name: string; tagline: string; description: string; comingSoon?: boolean }
> = {
  berserker: {
    name: 'Berserker',
    tagline: 'Carve through the horde up close',
    description:
      'A melee juggernaut who thrives in the thick of the swarm. Soak the punishment, swing harder, and turn every blade into a wall between your team and the dark.',
  },
  commando: {
    name: 'Commando',
    tagline: 'Spot stalkers, shred everything',
    description:
      'The squad\'s eyes and trigger-finger — reveals cloaked stalkers and lays down relentless full-auto fire to keep the trash zeds from ever closing in.',
  },
  demolitions: {
    name: 'Demolitions',
    tagline: 'Big crowds, bigger explosions',
    description:
      'When the wave overwhelms, the Demo answers with high explosives. Clear packed corridors and stagger the giants with grenades, pipes and a well-placed rocket.',
  },
  fieldmedic: {
    name: 'Field Medic',
    tagline: 'Keep the squad breathing',
    description:
      'The lifeline of any team that wants to survive Hell on Earth. Dart wounded teammates back to full from across the map while never quite running dry.',
  },
  firebug: {
    name: 'Firebug',
    tagline: 'Light it all on fire',
    description:
      'Burn the horde to ash with napalm, flame and ground-fire that punishes anything foolish enough to crowd you. Stay mobile, stay lit, and watch the swarm cook.',
  },
  gunslinger: {
    name: 'Gunslinger',
    tagline: 'Twin pistols, dead-eye aim',
    description:
      'A fast-draw specialist dual-wielding hand cannons. Pop heads in a blink, weave between zeds, and stack precision kills faster than anyone on the server.',
  },
  sharpshooter: {
    name: 'Sharpshooter',
    tagline: 'One shot, one headshot',
    description:
      'The long-range executioner who deletes Scrakes and Fleshpounds before they ever reach the team. Patience, precision, and a single perfect bullet.',
  },
  support: {
    name: 'Support Specialist',
    tagline: 'Shotguns, ammo, and walls of lead',
    description:
      'The frontline anchor: door-welding, ammo-sharing and unloading devastating shotgun blasts that erase entire ranks of zeds in a single pull.',
  },
  chemist: {
    name: 'Chemist',
    tagline: 'Toxins, gas and corrosive death',
    description:
      'A custom Uliunai perk that weaponizes chemistry — choke the horde in lingering clouds of poison and melt the big ones with corrosive payloads.',
  },
  engineer: {
    name: 'Engineer',
    tagline: 'Nail guns and deployable turrets',
    description:
      'A custom Uliunai perk that fortifies the battlefield — pin zeds to the wall with a rapid-fire nailgun and drop portal turrets that hold the line while you reload.',
  },
  hunter: {
    name: 'Hunter',
    tagline: 'Track, chase, take the trophy',
    description:
      'A relentless predator built to run down the biggest threats on the map. Fast, ruthless and always on the trail of the next kill — the apex perk of the Uliunai roster.',
  },
};

/** Perk display order (11 perks). */
const PERK_ORDER = [
  'berserker',
  'commando',
  'demolitions',
  'fieldmedic',
  'firebug',
  'gunslinger',
  'sharpshooter',
  'support',
  'chemist',
  'engineer',
  'hunter',
] as const;

/**
 * Perks that have prestige-tier icons but no wide banner art yet. Banners aren't
 * rendered anywhere today, but we still null these so the data stays truthful.
 */
const PERKS_WITHOUT_BANNER = new Set<string>(['firebug', 'engineer']);

// Weapon/arsenal data extracted from the ULMod veterancy source by
// kf1/tools/extract_perk_weapons.py. Re-run that script when HekuT updates perks.
const WEAPON_DATA = weaponData as Record<string, { weapons: PerkWeapon[] }>;
const BONUS_DATA = bonusData as Record<string, string[][]>;

export const PERKS: Perk[] = PERK_ORDER.map((key) => {
  const copy = PERK_COPY[key];
  const comingSoon = copy.comingSoon ?? false;
  return {
    key,
    name: copy.name,
    tagline: copy.tagline,
    description: copy.description,
    bannerSrc: comingSoon || PERKS_WITHOUT_BANNER.has(key) ? null : `/perks/banners/${key}.webp`,
    icons: comingSoon ? [] : buildTiers(key),
    weapons: WEAPON_DATA[key]?.weapons ?? [],
    bonuses: BONUS_DATA[key] ?? [],
    comingSoon,
  };
});

/** Look up a perk by its key. */
export function getPerk(key: string | null | undefined): Perk | undefined {
  if (!key) return undefined;
  return PERKS.find((p) => p.key === key);
}
